import { useCallback, useEffect, useState } from 'react';
import { Text } from '@nimbus-ds/components';
import { v4 as uuidv4 } from 'uuid';
import { i18nResponseDto } from '@tiendanube/common';
import { Thumbnail } from '@tiendanube/components';
import { TrashIcon } from '@tiendanube/icons';
import { ModalConfirmationBase } from 'App/components';
import ModalAside from 'App/components/lab/ModalAside';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { AlertImagesValidation, HeaderTop } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useImageLoader } from 'domains/Catalog/Products/pages/hooks';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import Sortable from './Sortable';
import FileDraggable from '../../../FileDraggable';
import { ImageGalleryState } from '../../ImageGallery';
import EditImageModal from '../EditImageModal';
import { AltTextGenerationContext } from '../EditImageModal/EditImageActions/components/EditAltTextModal/components/AltTextGenerationModal';
import { EditedImage } from '../EditImageModal/EditImageModal';
import UploadingMessage from '../UploadingMessage';

import './ImageGalleryModal.scss';

interface ImageGalleryModalInterface {
  isShow: boolean;
  images: ImageGalleryState[];
  imageIdForEditOnOpen?: string;
  message?: string;
  isOpenFileSystem?: boolean;
  altTextGenerationContext: AltTextGenerationContext;
  onClose: () => void;
  onChange: (images: ImageGalleryState[]) => void;
  onError: () => void;
  onUploadImages: (images: ImageGalleryState[]) => void;
  onOpenEditModal: () => void;
  onClickVideo: () => void;
}

function ImageGalleryModal({
  isShow,
  images,
  imageIdForEditOnOpen,
  message = '',
  isOpenFileSystem = false,
  altTextGenerationContext,
  onClose,
  onChange,
  onError,
  onUploadImages,
  onOpenEditModal,
  onClickVideo,
}: ImageGalleryModalInterface) {
  const t = useTranslationCatalog();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);
  const [isSelectMode, setIsSelectMode] = useState<boolean>(false);
  const [imagesSelected, setImagesSelected] = useState<string[]>([]);
  const [image, setImage] = useState<ImageGalleryState | null>(null);
  const [timesNewElement, setTimesNewElement] = useState(1);

  const [showModalRemoveImage, openModalRemoveImage, closeModalRemoveImage] =
    useModal();
  const [isShowEditPhotoModal, openEditPhotoModal, closeEditPhotoModal] =
    useModal();

  const videoQuantity = images.filter(
    (file) => file.mediaType === 'video',
  ).length;

  const {
    imagesWithBadType,
    imagesExceededSize,
    videosExceededSize,
    videosExceededQuantity,
    acceptTypeFile,
    setImagesWithBadType,
    setImagesExceededSize,
    setVideosExceededSize,
    setVideosExceededQuantity,
    handleOnchange,
  } = useImageLoader({
    onUploadImages,
    videoQuantity,
    allowUploadVideos: true,
    countItems: images.length,
  });

  const handleOnDrop = (files: File[]) => {
    handleOnchange(files);
  };

  const handleOnClose = () => {
    setIsSelectMode(false);
    setImagesSelected([]);
    setImagesExceededSize([]);
    setVideosExceededSize([]);
    setVideosExceededQuantity(false);
    setImagesWithBadType([]);
    setTimesNewElement(1);
    onClose();
  };

  const handleOnClickRemove = (id: string) => {
    handleSelect(id);
    openModalRemoveImage();
  };

  const handleOnClickEdit = useCallback(
    (id: string) => {
      const image = images.find((image) => image.id === id);

      if (image) {
        setImage(image);
        openEditPhotoModal();
      }
    },
    [images, setImage, openEditPhotoModal],
  );

  useEffect(() => {
    if (imageIdForEditOnOpen) {
      handleOnClickEdit(imageIdForEditOnOpen);
      onOpenEditModal();
    }
  }, [imageIdForEditOnOpen, handleOnClickEdit, onOpenEditModal]);

  const handleApplyRemoveImages = () => {
    onChange(images.filter((image) => !imagesSelected.includes(image.id)));
    setImagesSelected([]);
    setImagesExceededSize([]);
    setVideosExceededSize([]);
    setVideosExceededQuantity(false);
    setIsSelectMode(false);
    closeModalRemoveImage();
  };

  const handleSelectImage = () => setIsSelectMode(!isSelectMode);

  const handleSelect = (idSelected: string) => {
    if (imagesSelected.includes(idSelected)) {
      setImagesSelected((ids) => ids.filter((id) => id !== idSelected));
    } else {
      setImagesSelected((ids) => [...ids, idSelected]);
    }
  };

  const handleOnCloseRemoveModal = () => {
    setImagesSelected([]);
    closeModalRemoveImage();
  };

  const handleOnClickImage = (image: ImageGalleryState) => {
    setImage(image);
  };

  const handleOnChangeAltText = (value: i18nResponseDto) => {
    onChange(
      images.map((img) => {
        if (img.id === image?.id) {
          return {
            ...img,
            alt: value,
          };
        }
        return img;
      }),
    );
    setImage(null);
  };

  const isSelected = !!imagesSelected.length;
  const labelAction =
    isSelectMode && !isSelected
      ? t('products.modal.cancel')
      : isSelected
      ? t('products.modal.remove')
      : t('products.modal.selectToRemove');

  const backNavigation = {
    onClick: handleOnClose,
    children: '',
  };

  const headerAction = {
    appearance: isSelected ? ('danger' as const) : undefined,
    icon: !(isSelectMode && !isSelected) ? TrashIcon : undefined,
    children: labelAction,
    onClick: isSelected ? openModalRemoveImage : handleSelectImage,
  };

  const onSaveEditedImage = (
    editedImage: EditedImage,
    replacedImageId: string,
  ) => {
    const imageToUpload: ImageGalleryState = {
      isLoading: true,
      id: uuidv4(),
      isError: false,
      src: editedImage.base64,
      alt: image?.alt || {},
      base64: editedImage.base64,
      isEdited: true,
      replacedImageId: replacedImageId,
      name: '',
      type: '',
      size: 0,
      mimeType: '',
      mediaType: 'image',
      videoUrl: '',
    };
    onUploadImages([imageToUpload]);
  };

  return (
    <ModalAside
      hasVirtualizationContent
      isOpen={isShow}
      onDidDismiss={handleOnClose}
      headerTop={
        <HeaderTop navigation={backNavigation} mainAction={headerAction} />
      }
      headerContent={
        hasUploadVideos ? t('products.modal.media') : t('products.modal.photos')
      }
    >
      <Text>
        {hasUploadVideos
          ? t('products.modal.orderPhotosAndVideo')
          : t('products.modal.orderPhotos')}
      </Text>
      <AlertImagesValidation filenames={imagesWithBadType} validation="type" />
      <AlertImagesValidation filenames={imagesExceededSize} validation="size" />
      <AlertImagesValidation
        filenames={videosExceededSize}
        validation="videoSize"
      />
      {videosExceededQuantity && (
        <AlertImagesValidation filenames={['']} validation="videoQuantity" />
      )}

      <UploadingMessage message={message} />
      <Sortable
        items={images}
        isSelectMode={isSelectMode}
        imagesSelected={imagesSelected}
        onSelected={handleSelect}
        onRemove={handleOnClickRemove}
        onEdit={handleOnClickEdit}
        onOrder={onChange}
        onError={onError}
        ariaLabelError={t('products.details.errorUploadPhoto')}
        onClickImage={handleOnClickImage}
        onClickVideo={onClickVideo}
        newFileElement={
          <FileDraggable onDropFiles={handleOnDrop}>
            <div className="opacable">
              <Thumbnail.File
                key={timesNewElement}
                // This key allows re-selecting the same file (otherwise onChange won't fire).
                // Assigning a new key on each onChange forces a re-render.
                onChange={(e) => {
                  setTimesNewElement((value) => value + 1);
                  handleOnchange(e);
                }}
                ariaLabel={t('products.modal.addPhotos')}
                disabled={isSelectMode}
                open={isOpenFileSystem && timesNewElement === 1}
                accept={acceptTypeFile}
              />
            </div>
          </FileDraggable>
        }
      />
      <ModalConfirmationBase
        title={t('products.modal.deleteConfirmationModal.title', {
          count: imagesSelected.length,
        })}
        text={t('products.modal.deleteConfirmationModal.body', {
          count: imagesSelected.length,
        })}
        labelCancel={t('products.modal.cancel')}
        label={t('products.modal.remove')}
        show={showModalRemoveImage}
        onConfirm={handleApplyRemoveImages}
        onCancel={handleOnCloseRemoveModal}
      />
      {!!image && (
        <EditImageModal
          image={image}
          onChangeAltText={handleOnChangeAltText}
          onSaveImage={onSaveEditedImage}
          isShow={isShowEditPhotoModal}
          altTextGenerationContext={altTextGenerationContext}
          onClose={closeEditPhotoModal}
        />
      )}
    </ModalAside>
  );
}

export default ImageGalleryModal;
